"use client"

import { useGetTrigger } from "@/hooks/triggers"
import { LoadingSpinner } from "@/components/loading-spinner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"
import { T } from "@/i18n"

interface TriggerViewProps {
  triggerId: string
  onBack: () => void
}

export function TriggerView({ triggerId, onBack }: TriggerViewProps) {
  const { data: trigger, isLoading, isError } = useGetTrigger(triggerId)

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <LoadingSpinner />
      </div>
    )
  }

  if (isError || !trigger) {
    return (
      <div className="text-center p-8">
        <p className="text-red-500">
          <T tKey="operations.triggers.view.error" />
        </p>
      </div>
    )
  }

  return (
    <div>
      <Button variant="ghost" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        <T tKey="common.back" />
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>{trigger.name}</CardTitle>
          <CardDescription>
            <Badge variant={trigger.active ? "default" : "secondary"}>
              {trigger.active ? <T tKey="common.active" /> : <T tKey="common.inactive" />}
            </Badge>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.triggers.fields.entityType" />
            </h3>
            <p>{trigger.entityType}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.triggers.fields.description" />
            </h3>
            <p>
              {trigger.description || (
                <span className="italic">
                  <T tKey="common.noDescription" />
                </span>
              )}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.triggers.fields.createdAt" />
            </h3>
            <p>{new Date(trigger.createdAt).toLocaleString()}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              <T tKey="operations.triggers.fields.updatedAt" />
            </h3>
            <p>{new Date(trigger.updatedAt).toLocaleString()}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
